import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type AccessReason =
  | 'paused'
  | 'archived'
  | 'no_coach'
  | 'payment_due'
  | 'payment_overdue'
  | null;

export interface ClientAccess {
  loading: boolean;
  allowed: boolean;
  reason: AccessReason;
  coachId: string | null;
  pauseUntil: string | null;
  pauseReason: string | null;
}

const DEFAULT: ClientAccess = {
  loading: true,
  allowed: false,
  reason: null,
  coachId: null,
  pauseUntil: null,
  pauseReason: null,
};

// Cache key per user — guarda el último estado de acceso conocido para
// que offline el cliente NO sea bloqueado con "Sin coach asignado"
// solo porque no hay red. La RPC client_access_status falla offline,
// y antes el código asumía "no_coach" por defecto → pantalla bloqueada.
//
// Ahora: si tenemos un resultado cacheado (de cuando había red), lo
// usamos en vez de la pantalla de bloqueo. La verificación real
// vuelve a correr al recuperar conexión vía 'iftmx:reconnected'.
const accessCacheKey = (uid: string) => `client_access_${uid}`;

function readCache(uid: string): ClientAccess | null {
  try {
    const raw = localStorage.getItem(accessCacheKey(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { ...parsed, loading: false };
  } catch {
    return null;
  }
}

function writeCache(uid: string, access: Omit<ClientAccess, 'loading'>) {
  try {
    localStorage.setItem(accessCacheKey(uid), JSON.stringify(access));
  } catch {
    // Storage quota / private mode — fallar silenciosamente.
  }
}

/**
 * Source of truth on the frontend for "can this client use the app?".
 *
 * Wraps the SECURITY DEFINER RPC `client_access_status` defined in
 * migration 20260507000002. The same RPC is consulted by the AI edge
 * functions, so the frontend's gate matches the server's exactly.
 *
 * Accepts a userId for explicit control (used in tests / onboarding).
 * If omitted, reads the current auth user.
 *
 * Subscribes to realtime changes on `coach_clients` for the user so
 * pausing/archiving/reactivating from the coach panel is reflected
 * instantly on the client device.
 */
export function useClientAccess(userId?: string | null): ClientAccess {
  const [state, setState] = useState<ClientAccess>(DEFAULT);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      let uid = userId ?? null;
      if (!uid) {
        const { data: { user } } = await supabase.auth.getUser();
        uid = user?.id ?? null;
      }
      if (!uid) {
        if (!cancelled) setState({ ...DEFAULT, loading: false });
        return null;
      }

      // Si NO hay red, evitamos esperar el timeout de la RPC y vamos
      // directo al caché. Si tampoco hay caché, mostramos un estado
      // permisivo provisional (allowed=true) — el bloqueo real se
      // aplicará cuando vuelva la red. Mejor pecar de permisivo que
      // dejar al cliente fuera de su propia app sin razón.
      if (!navigator.onLine) {
        const cached = readCache(uid);
        if (!cancelled) {
          setState(cached ?? {
            loading: false, allowed: true, reason: null,
            coachId: null, pauseUntil: null, pauseReason: null,
          });
        }
        return uid;
      }

      const { data, error } = await supabase.rpc('client_access_status', { uid });
      if (cancelled) return uid;

      // Errores de red en línea: si tenemos caché, lo usamos en vez
      // de bloquear. Sin caché caemos al estado conservador anterior.
      if (error || !data || data.length === 0) {
        const cached = readCache(uid);
        if (cached) {
          setState(cached);
        } else if (error) {
          // Hubo error de red genuino y no hay caché — asumimos
          // permisivo provisional para no bloquear injustamente.
          setState({
            loading: false, allowed: true, reason: null,
            coachId: null, pauseUntil: null, pauseReason: null,
          });
        } else {
          // No hubo error pero la RPC devolvió vacío → genuinamente
          // no tiene coach (caso normal del primer login post-pago).
          setState({
            loading: false, allowed: false, reason: 'no_coach',
            coachId: null, pauseUntil: null, pauseReason: null,
          });
        }
        return uid;
      }

      const row = data[0] as any;
      const fresh: Omit<ClientAccess, 'loading'> = {
        allowed: row.allowed === true,
        reason: row.reason as AccessReason,
        coachId: row.coach_id ?? null,
        pauseUntil: row.pause_until ?? null,
        pauseReason: row.pause_reason ?? null,
      };
      writeCache(uid, fresh);
      setState({ loading: false, ...fresh });
      return uid;
    };

    // Recargar la verificación al volver la conexión sin tener que
    // cerrar/abrir la app. Antes el usuario veía "Sin coach asignado"
    // y no había forma de recuperarse sin reload manual.
    const onReconnected = () => { resolve(); };
    window.addEventListener('iftmx:reconnected', onReconnected);

    let channel: ReturnType<typeof supabase.channel> | null = null;
    resolve().then((uid) => {
      if (!uid || cancelled) return;
      // Reload the gate when this client's coach link changes (pause /
      // archive / reactivate). Kicks the panel update across devices.
      channel = supabase
        .channel(`client-access-${uid}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'coach_clients', filter: `client_id=eq.${uid}` },
          () => { resolve(); },
        )
        .subscribe();
    });

    return () => {
      cancelled = true;
      window.removeEventListener('iftmx:reconnected', onReconnected);
      if (channel) supabase.removeChannel(channel);
    };
  }, [userId]);

  return state;
}
